import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import ApiContext from '@/context/ApiContext/ApiContext';
import HealthStatusContext from '@/context/HealthStatusContext/HealthStatusContext';
import { Me, Role } from '@/hooks/api/me/types';
import useLocale from '@/hooks/useLocale';

interface IUseGetMe {
  data?: Me;
  isPending: boolean;
  isAdmin?: boolean;
}

const useGetMe = (enabled = true): IUseGetMe => {
  const { client } = useContext(ApiContext);
  const { setLanguage } = useLocale();
  const { areSystemsAvailable } = useContext(HealthStatusContext);

  const { data, isPending } = useQuery<Me, AxiosError>({
    queryKey: ['api', 'mhp-web', 'me'],
    queryFn: async ({ queryKey }) => {
      const result = (await client.current!.get<Me>(queryKey.join('/'), )).data;
      await setLanguage(result?.language?.toLowerCase());
      return result;
    },
    enabled: areSystemsAvailable && enabled && !!client.current,
    staleTime: 30 * 60 * 1000, // 30 minutes
    // Note: onSuccess is no longer supported in React Query v5 - moved side effect to queryFn
  });

  const isAdmin = !!data?.roles?.includes(Role.ADMIN);

  return { data, isAdmin, isPending };
};

export default useGetMe;
